(ns minorodata.components.analytics-models.panel
  (:require ["lucide-react" :refer [AlertTriangle ArrowRight Pencil PlayCircle Plus RefreshCw Sliders Trash2]]
            [react :as react]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.components.analytics-models.docs :as docs]
            [minorodata.components.analytics-models.forms :as forms]
            [minorodata.components.analytics-models.overview :as overview]
            [minorodata.components.pipeline-editor.utils :refer [tab-icon]]
            [re-frame.core :as re-frame]
            [re-posh.core :as re-posh]
            [ui.button :as ui.button]
            [ui.dialog :as ui.dialog]
            [ui.drawer :as ui.drawer]
            [ui.skeleton :refer [skeleton]]
            [ui.typography :as typography]
            [ui.utils :refer [clsx]]))


(defn- navigate-to-catalogue []
  (dispatch [:app.events/navigate :analytics-models]))

(defn- io-summary [{:keys [input-tables output-tables]}]
  (let [ins  (count input-tables)
        outs (count output-tables)]
    (when (or (pos? ins) (pos? outs))
      [:div {:class "flex items-center gap-1.5 text-xs text-kleene-text/70"}
       [:span (str ins " input" (when (not= 1 ins) "s"))]
       [:> ArrowRight {:size 12 :class "text-kleene-text/40"}]
       [:span (str outs " output" (when (not= 1 outs) "s"))]])))


(defn- params-summary [custom-ui-blocks]
  (let [n (count custom-ui-blocks)]
    (when (pos? n)
      [:div {:class "flex items-center gap-1 text-xs text-kleene-text/70"}
       [:> Sliders {:size 12 :class "text-kleene-text/40"}]
       [:span (str n " param" (when (not= 1 n) "s"))]])))


(defn- model-card
  "Catalogue card. The docs trigger is a sibling of the (card-wide) pick button
   rather than a child of it — a button can't nest inside a button — so it sits
   above it, absolutely positioned over the header row's reserved padding."
  [{:analytics-model-registry/keys [project-name display-name description-brief
                                    version table-definitions custom-ui-blocks]
    :as model}]
  [:div {:class "relative h-full"}
   [:button
    {:type     "button"
     :class    (clsx "h-full w-full text-left rounded-lg border bg-kleene-background p-4 flex flex-col gap-2"
                     "transition-colors hover:border-kleene-accent hover:shadow-sm")
     :data-cy  (str "analytics-model-pick-" project-name)
     :on-click #(dispatch [:app.events/navigate
                           :analytics-model-create
                           {:project-name project-name}])}
    [:div {:class "flex items-start justify-between gap-2 pr-8"}
     [typography/h4 {:class "text-base leading-snug"}
      (or display-name project-name)]
     (when version
       [:span {:class (clsx "shrink-0 rounded-full px-2 py-0.5 text-[10px]"
                            "bg-kleene-background-secondary text-kleene-text/70")}
        version])]
    (when description-brief
      [:p {:class "text-xs text-kleene-text/70 line-clamp-2"}
       description-brief])
    [:div {:class "mt-auto pt-3 flex flex-wrap items-center gap-x-3 gap-y-1"}
     [io-summary table-definitions]
     [params-summary custom-ui-blocks]]]
   [docs/docs-button {:model   model
                      :class   "absolute top-3 right-3 z-10"
                      :data-cy (str "analytics-model-docs-" project-name)}]])


(defn- card-skeleton []
  [:div {:class "rounded-lg border bg-kleene-background p-4 flex flex-col gap-3"}
   [skeleton {:width "70%" :height 18}]
   [skeleton {:height 12}]
   [skeleton {:width "60%" :height 12}]])


(defn- models-catalogue []
  (let [models   @(re-frame/subscribe [:analytics-model-registry/pull-many])
        loading? @(re-posh/subscribe [:mold-http/loading? :analytics-models.api/fetch-model-registry])]
    [:div {:class "container p-6"}
     [:div {:class "mb-4"}
      [typography/h2 "Available models"]
      [:p {:class "text-sm text-kleene-text/70 mt-1"}
       "Pick a model to create a new instance."]]
     (cond
       loading?
       [:div {:class "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-3"}
        (for [i (range 4)]
          ^{:key i} [card-skeleton])]

       (empty? models)
       [:div {:class "py-8 text-center text-kleene-text/60"} "No analytics models available"]

       :else
       [:div {:class "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-3"}
        (for [model models]
          ^{:key (:analytics-model-registry/project-name model)}
          [model-card model])])]))


(def ^:private drawer-content-class
  ;; Wider than the default drawer (w-96). `!` beats the base width; max-w keeps
  ;; it responsive. overflow-y-auto so the wizard's sticky footer pins correctly.
  "w-4xl max-w-full pb-0")


(defn- drawer-header [{:keys [title description model]}]
  [:div {:class "flex gap-2"}
   [:div [tab-icon {:type "model" :size 20 :class "shrink-0 p-2 rounded-lg"}]]
   [:div {:class "grow"}
    [ui.drawer/drawer-title {:class "flex items-center gap-2"} title]
    (when description
      [ui.drawer/drawer-description description])]
   [docs/docs-button {:model model :label "Model docs" :variant :secondary :class "shrink-0"}]])


(defn- create-drawer [{:keys [open? params]}]
  (let [project-name (:project-name params)
        model        @(re-posh/subscribe [:analytics-models.subs/registry-model project-name])
        display-name (:analytics-model-registry/display-name model)]
    [ui.drawer/drawer-root {:open           open?
                            :on-open-change #(when-not % (navigate-to-catalogue))}
     [ui.drawer/drawer-content {:class drawer-content-class}
      [drawer-header {:title       (str "Create '" (or display-name project-name) "' model instance")
                      :description (:analytics-model-registry/description-brief model)
                      :model       model}]
      [ui.drawer/drawer-body
       (when model
         [forms/create-instance
          {:project-name project-name
           :model        model
           :version      (:analytics-model-registry/version model)
           :on-close     navigate-to-catalogue}])]]]))


(defn- edit-drawer [{:keys [open? id]}]
  (let [instance      @(re-posh/subscribe [:analytics-models.subs/model id])
        project-name  (:analytics-model/registry-model instance)
        model         @(re-posh/subscribe [:analytics-models.subs/registry-model project-name])
        instance-name (:analytics-model/name instance)
        close         #(dispatch [:app.events/navigate
                                  :analytics-model-instance-details {:instance-id id}])]
    [ui.drawer/drawer-root {:open           open?
                            :on-open-change #(when-not % (close))}
     [ui.drawer/drawer-content {:class drawer-content-class}
      [drawer-header {:title       (str "Edit \"" instance-name "\"")
                      :description (:analytics-model-registry/description-brief model)
                      :model       model}]
      [ui.drawer/drawer-body
       (when (and instance model)
         [forms/edit-instance {:id           id
                               :project-name project-name
                               :model        model
                               :instance     instance}])]]]))


(defn- delete-instance-dialog [{:keys [id instance-name]} trigger]
  [ui.dialog/dialog-root
   [ui.dialog/dialog-trigger trigger]
   [ui.dialog/dialog-content
    [ui.dialog/dialog-title {:class "mb-6"}
     (str "Do you really wish to delete "
          (if instance-name
            (str "instance \"" instance-name "\"")
            "this instance")
          "?")]
    [ui.dialog/dialog-actions
     [ui.dialog/dialog-close
      [ui.button/button
       {:variant  :destructive
        :on-click (fn []
                    (dispatch [:analytics-models.api/delete-model
                               {:id         id
                                :on-success #(dispatch [:app.events/navigate :analytics-models])}]))
        :data-cy  "delete-analytics-model-instance-confirm-btn"}
       "Delete instance"]]]]])


(defn- running?
  "True while a run is in flight. `:mold-http/loading?` only covers the POST, so
   on its own it would let a second click start a duplicate job mid-run."
  [id]
  (let [run-status @(re-frame/subscribe [:analytics-models.subs/last-run-status id])]
    (or @(re-posh/subscribe [:mold-http/loading? :analytics-models.api/run-model])
        (:is-executing run-status)
        (= "pending" (:status run-status)))))


(defn- instance-actions [{:keys [id instance-name]}]
  [:div {:class "shrink-0 flex items-center gap-2"}
   [ui.button/button
    {:variant  :secondary
     :icon     PlayCircle
     :disabled (boolean (running? id))
     :on-click #(dispatch [:analytics-models.api/run-model {:id id}])
     :data-cy  "run-analytics-model-instance-btn"}
    "Run"]
   [ui.button/button
    {:variant  :secondary
     :icon     Pencil
     :on-click #(dispatch [:app.events/navigate
                           :analytics-model-instance-edit
                           {:instance-id id}])
     :data-cy  "edit-analytics-model-instance-btn"}
    "Edit"]
   [delete-instance-dialog {:id id :instance-name instance-name}
    [:div
     [ui.button/button
      {:variant   :error-border
       :icon      Trash2
       :component :div
       :data-cy   "delete-analytics-model-instance-btn"}
      "Delete"]]]])


;; Refresh the registry catalogue entry for this instance's model whenever the
;; instance is visited, so the out-of-date check reflects the latest build at
;; view time (renders nothing — it only owns the effect).
(defn- fetch-latest-on-visit [{:keys [project-name]}]
  (react/useEffect
   (fn []
     (when project-name
       (dispatch [:analytics-models.api/fetch-model-registry-version project-name]))
     js/undefined)
   #js [project-name])
  nil)


(defn- up-version-dialog [{:keys [id latest]} trigger]
  [ui.dialog/dialog-root
   [ui.dialog/dialog-trigger trigger]
   [ui.dialog/dialog-content
    [ui.dialog/dialog-title {:class "mb-2"} "Up-version model instance"]
    [typography/p {:class "mb-6 text-kleene-text-secondary"}
     "This re-pins the instance to the latest build of version "
     [:span {:class "font-medium text-kleene-text"} (:version latest)]
     ". Your configuration and schedule are kept."]
    [ui.dialog/dialog-actions
     [ui.dialog/dialog-close
      [ui.button/button
       {:variant  :primary
        :on-click (fn []
                    (dispatch [:analytics-models.api/up-version-model
                               {:id         id
                                :on-success (fn [updated]
                                              (dispatch [:app.events/navigate
                                                         :analytics-model-instance-details
                                                         {:instance-id (:analytics-model/model-id updated)}]))}]))
        :data-cy  "analytics-model-up-version-confirm-btn"}
       "Up-version"]]]]])


;; Out-of-date indicator. A `:revision` change (newer image, same version) is
;; up-versionable in place; a `:version` change may carry schema/UI drift, so we
;; route the user to re-create the instance instead.
(defn- version-banner [{:keys [id project-name status]}]
  (when-let [{:keys [change-type latest]} status]
    (let [revision? (= change-type :revision)]
      [:div {:class   (clsx "flex items-center justify-between gap-4 rounded-lg border p-4"
                            "border-kleene-warning-02/40 bg-kleene-warning-background")
             :data-cy "analytics-model-out-of-date-banner"}
       [:div {:class "flex items-start gap-3"}
        [:> AlertTriangle {:size 18 :class "mt-0.5 shrink-0 text-kleene-text-warning"}]
        [:div
         [typography/label {:class "text-kleene-text"}
          (if revision? "Update available" "New version available")]
         [typography/p {:class "text-xs text-kleene-text-secondary"}
          (if revision?
            "A newer build of this model version has been deployed. Up-version to run the latest image."
            (str "Version " (:version latest) " of this model is available. Re-create this "
                 "instance to move to it — a new version may change its inputs or parameters."))]]]
       (if revision?
         [up-version-dialog {:id id :latest latest}
          [:div
           [ui.button/button
            {:variant   :warning
             :size      :small
             :icon      RefreshCw
             :component :div
             :data-cy   "analytics-model-up-version-btn"}
            "Up-version"]]]
         [ui.button/button
          {:variant  :warning-border
           :size     :small
           :icon     Plus
           :on-click #(dispatch [:app.events/navigate
                                 :analytics-model-create
                                 {:project-name project-name}])
           :data-cy  "analytics-model-recreate-btn"}
          "Create new instance"])])))


(defn- overview-instance [{:keys [id]}]
  (let [instance      @(re-posh/subscribe [:analytics-models.subs/model id])
        project-name  (:analytics-model/registry-model instance)
        model         @(re-posh/subscribe [:analytics-models.subs/registry-model project-name])
        instance-name (:analytics-model/name instance)
        status        @(re-frame/subscribe [:analytics-models.subs/version-status id])]
    [:div {:class "w-4xl p-6 pb flex flex-col gap-6"}
     [:f> fetch-latest-on-visit {:project-name project-name}]
     [:header {:class "flex items-start justify-between gap-4"}
      [:div
       [typography/h2 (or instance-name "Model instance")]
       [typography/p {:class "mt-1 text-kleene-text-secondary"}
        (:analytics-model-registry/description-brief model)]]
      [:div {:class "shrink-0 flex items-center gap-2"}
       [docs/docs-button {:model model :label "Model docs" :variant :secondary}]
       (when instance
         [instance-actions {:id id :instance-name instance-name}])]]
     (when instance
       [version-banner {:id id :project-name project-name :status status}])
     (when (and instance model)
       [overview/overview {:instance instance :model model}])]))


(defn panel []
  (let [{:app-route/keys [name params]} @(re-posh/subscribe [:app-route/pull])
        instance-id (:instance-id params)
        instance?   (#{:analytics-model-instance-details :analytics-model-instance-edit} name)]
    [:div {:class "flex flex-col h-full overflow-hidden"}
     [:div {:class "flex-1 overflow-auto"}
      ;; Background content; create/edit open as side drawers over it.
      (if instance?
        [overview-instance {:id instance-id}]
        [models-catalogue])]
     [create-drawer {:open? (= name :analytics-model-create) :params params}]
     [edit-drawer {:open? (= name :analytics-model-instance-edit) :id instance-id}]]))
